import { capture } from '@/utils/analytics';
import type {
  AudioOverviewFormat,
  AudioOverviewLength,
  AudioOverviewSourceKind,
  AudioOverviewSummary,
} from '@/types/AudioOverview';

/** PostHog events for Audio Overviews (ORA-735). Props are camelCase. */

export type AudioOverviewUpgradeSource =
  /** A free student pressed Generate in the config form. */
  | 'generate'
  /** Create or retry came back 403 AUDIO_OVERVIEW_UPGRADE_REQUIRED. */
  | 'generate_rejected'
  /** A free student tapped a locked overview. */
  | 'locked_overview'
  /** The audio URL or a progress save came back 403 (the plan lapsed). */
  | 'playback_rejected'
  /** `/config` turned locked while an episode was loaded. */
  | 'plan_changed';

export type AudioOverviewOpenSource = 'tile' | 'header' | 'article_toolbar';

interface AudioOverviewSourceProps {
  sourceKind?: AudioOverviewSourceKind;
  articleId?: string | null;
}

const sourceProps = ({
  sourceKind = 'lecture',
  articleId = null,
}: AudioOverviewSourceProps) => ({ sourceKind, articleId });

export const captureAudioOverviewTileOpened = (
  props: {
    locked: boolean;
    lectureCount: number;
    source: AudioOverviewOpenSource;
  } & AudioOverviewSourceProps
): void =>
  capture('audio_overview_tile_opened', {
    ...props,
    ...sourceProps(props),
  });

export const captureAudioOverviewGenerateClicked = (
  props: {
    locked: boolean;
    format: AudioOverviewFormat;
    length: AudioOverviewLength;
    lectureCount: number;
    hasFocusNotes: boolean;
    source?: AudioOverviewOpenSource;
  } & AudioOverviewSourceProps
): void =>
  capture('audio_overview_generate_clicked', {
    ...props,
    ...sourceProps(props),
  });

export const captureAudioOverviewUpgradePrompted = (
  source: AudioOverviewUpgradeSource,
  context: AudioOverviewSourceProps = {}
): void =>
  capture('audio_overview_upgrade_prompted', {
    source,
    ...sourceProps(context),
  });

const overviewProps = (overview: AudioOverviewSummary) => ({
  overviewId: overview.id,
  format: overview.format,
  length: overview.length,
  lectureCount: overview.lectures.length,
  durationSec: overview.durationSec,
  sourceKind: overview.source,
  articleId: overview.articleId,
});

export const captureAudioOverviewPlayed = (
  overview: AudioOverviewSummary,
  { resumed, replay }: { resumed: boolean; replay: boolean }
): void =>
  capture('audio_overview_played', {
    ...overviewProps(overview),
    resumed,
    replay,
  });

export const captureAudioOverviewCompleted = (
  overview: AudioOverviewSummary
): void => capture('audio_overview_completed', overviewProps(overview));
